/**
 * The machine marking of the Writing module.
 *
 * telc has licensed examiners mark this block against four criteria (pp. 33-34
 * of the telc document — see `structure.ts`). This app cannot put a licensed
 * examiner in front of the text, so it puts a model there instead, marking
 * against those same four criteria and the same 0-5 bands. What comes back is
 * an estimate; the interface has to say so.
 *
 * Only the shape lives here — the rubric text handed to the model and the call
 * that makes it are worker-side, so neither reaches the browser bundle.
 */
import {
  WRITING_CRITERIA,
  WRITING_MAX,
  type WritingCriterionId,
} from "./structure.ts";
import type { WritingTask } from "./schema.ts";

/**
 * Longest text we will mark, in words.
 *
 * A telc B1·B2 answer runs to roughly 100-150 words, so this is generous for
 * anyone writing the exam and tight enough that the block cannot be used as a
 * general-purpose proofreader. Enforced on the server, where the key is.
 */
export const WRITING_WORD_LIMIT = 250;

/**
 * Hard character cap applied before the word count.
 *
 * A word count alone is not a length limit: one "word" of 200 000 characters
 * passes it and still costs a fortune to send.
 */
export const WRITING_CHAR_LIMIT = 4000;

/**
 * Shortest text worth marking, in words.
 *
 * Marking starts by itself now, so anything a candidate leaves in the box costs
 * a call. "asdf" and a half-written greeting have nothing to mark against four
 * criteria, and telling someone their eight words score 2/20 teaches them
 * nothing they did not know.
 */
export const WRITING_MIN_WORDS = 25;

export const countWords = (text: string): number =>
  text.trim() ? text.trim().split(/\s+/).length : 0;

/** Where one correction's quote stands in the candidate's text. */
export interface CorrectionSpan {
  start: number;
  end: number;
  /** Index into `corrections`. */
  index: number;
}

/**
 * Finds each correction's quote in the text it was taken from, so the mistake
 * can be marked where it was written rather than only listed under the text.
 *
 * The quote is the candidate's own words, but the model may have joined a line
 * break into a space, so whitespace matches whitespace of any kind. A quote that
 * is not there, or that would overlap one already placed, is left out: the
 * correction is still listed, it just has no place to point at. Spans come back
 * in the order they stand in the text.
 */
export function locateCorrections(
  text: string,
  corrections: Pick<Correction, "quote">[],
): CorrectionSpan[] {
  const spans: CorrectionSpan[] = [];
  corrections.forEach(({ quote }, index) => {
    const words = quote.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return;
    const pattern = new RegExp(
      words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("\\s+"),
      "gu",
    );
    for (const match of text.matchAll(pattern)) {
      const start = match.index;
      const end = start + match[0].length;
      if (spans.some((s) => start < s.end && s.start < end)) continue;
      spans.push({ start, end, index });
      return;
    }
  });
  return spans.sort((a, b) => a.start - b.start);
}

/* ------------------------------------------------------------------ */
/* One task, one or two texts                                          */
/* ------------------------------------------------------------------ */

/**
 * A text the candidate has to write.
 *
 * telc's Writing block is one letter, so one part. The state exam's is two:
 * a short functional text (a notice, an invitation, greetings — 25-50 words)
 * and a long one (an e-mail, an essay — 150-175 words at B1), both from the
 * same chosen set, each with its own brief and its own length printed on the
 * paper. They are different texts in different forms and they are written in
 * different boxes; gluing them into one answer marked once was the app
 * pretending the paper asks for something it does not.
 */
export interface WritingPart {
  /** Position in the task, 0-based. Also where the text is stored. */
  index: number;
  /** "a" / "b" as the paper prints it; empty when the task is a single text. */
  label: string;
  brief: string;
  /** Guideline points. telc prints three; the state exam prints none. */
  points: string[];
  /** Words the paper asks for, or `null` when it does not say. */
  words: number | null;
  /** A photograph the brief is about; absent on every other task. */
  image?: string;
}

/** The texts a task asks for — always at least one. */
export function writingParts(task: WritingTask): WritingPart[] {
  if (task.parts?.length)
    return task.parts.map((p, i) => ({
      index: i,
      label: p.label,
      brief: p.brief,
      points: [],
      words: p.words,
      ...(p.image ? { image: p.image } : {}),
    }));
  return [
    {
      index: 0,
      label: "",
      brief: task.brief,
      points: task.points,
      words: null,
    },
  ];
}

/**
 * How far a text may miss its target length before the length itself is a
 * fault. The examining body allows 10% either way; below that the marking
 * should be about the writing, not about the word counter.
 */
export const LENGTH_TOLERANCE = 0.1;

export const lengthOff = (words: number, target: number | null): boolean =>
  target !== null &&
  (words < target * (1 - LENGTH_TOLERANCE) ||
    words > target * (1 + LENGTH_TOLERANCE));

/**
 * Every marking of one attempt's Writing block: one per text.
 *
 * Two texts get two markings and never one averaged verdict — a 30-word notice
 * and a 170-word essay are marked on different things, and a single set of four
 * marks covering both would be a number nobody could act on.
 */
export interface WritingReviewSet {
  parts: WritingReview[];
  /** The block's points, on the scale the paper uses. */
  points: number;
  /** What the block is worth on that paper. */
  max: number;
}

/**
 * Reads what is stored on an attempt.
 *
 * Markings written before the Writing block could hold two texts are a bare
 * review; they are one-part sets, and are read as such rather than migrated.
 */
export function reviewSetOf(stored: unknown): WritingReviewSet | null {
  if (!stored || typeof stored !== "object") return null;
  const value = stored as Partial<WritingReviewSet> & Partial<WritingReview>;
  if (Array.isArray(value.parts))
    return {
      parts: value.parts,
      points: value.points ?? 0,
      max: value.max ?? WRITING_MAX,
    };
  const one = stored as WritingReview;
  return { parts: [one], points: one.points, max: WRITING_MAX };
}

/**
 * The block's points, on the paper's own scale.
 *
 * Each text is marked 0-20 on the four criteria. telc's block *is* those 20
 * points, so a single text passes through unchanged. The state exam's block is
 * worth what the paper says (30 at B1) for both texts together, and the
 * examining body does not publish how that splits between them: the paper
 * prints three criteria for the block as a whole (`PAPER_WRITING_CRITERIA`),
 * and what each point stands for stays with the examiners. So the split here is ours: each text counts for as many words as the
 * paper asks of it, which puts the weight on the long text where the exam
 * plainly puts it. The interface says the split is ours.
 */
export function blockPoints(
  parts: { points: number; weight: number | null }[],
  max: number,
): number {
  if (parts.length === 0) return 0;
  const total = parts.reduce((s, p) => s + (p.weight ?? 1), 0) || parts.length;
  const share =
    parts.reduce((s, p) => s + (p.points / WRITING_MAX) * (p.weight ?? 1), 0) /
    total;
  // Half points, because that is the smallest thing a state-exam paper is
  // marked in; telc's single text lands on whole numbers by itself.
  return Math.round(share * max * 2) / 2;
}

/**
 * How a guideline point was covered (p. 34).
 *
 * `full` is telc's `++` (clearly, in detail, on task), `partial` its `+`
 * (understandable and on task), `missing` its `Ø`.
 */
export type Coverage = "full" | "partial" | "missing";

export interface GuidelineVerdict {
  /** Position of the guideline point in the task, 0-based. */
  index: number;
  coverage: Coverage;
  comment: string;
}

export interface CriterionVerdict {
  points: number;
  comment: string;
}

/** One concrete mistake, quoted from the text so the reader can find it. */
export interface Correction {
  /** The candidate's own words, verbatim — always Polish. */
  quote: string;
  /** The same passage put right, in Polish. */
  fix: string;
  /** Why, in the reader's own language. */
  why: string;
}

export interface WritingReview {
  /** Which of the briefs was answered, as stored on the attempt. */
  choice: number | null;
  /** Which text of that brief this marks — 0 when the task is a single text. */
  part?: number;
  /** "a" / "b", as the paper labels it; empty for a single text. */
  label?: string;
  /** Words the paper asked for, when it says. */
  targetWords?: number | null;
  /** Nothing was written for this text, so there was nothing to mark. */
  unwritten?: boolean;
  /** An off-topic text scores zero on all four criteria (p. 34). */
  offTopic: boolean;
  /** One entry per guideline point of the chosen brief, in order. */
  guidelines: GuidelineVerdict[];
  criteria: Record<WritingCriterionId, CriterionVerdict>;
  /** Mistakes worth fixing, quoted. Ordered by how much they cost. */
  corrections: Correction[];
  /** What to do differently next time. */
  improvements: string[];
  summary: string;
  /** Criterion marks, 0-5 each — the same numbers an examiner writes on S30. */
  marks: Record<WritingCriterionId, number>;
  /** Their sum, 0-20, after the off-topic rule. */
  points: number;
  words: number;
  /** Language the comments are written in: the interface language at the time. */
  lang: string;
  model: string;
  createdAt: number;
}

/**
 * Criterion I (Wykonanie zadania) from the coverage of the three guideline
 * points — the table on p. 34, transcribed.
 *
 * The model judges each point; the arithmetic stays here. Asking a model for
 * the total as well would let it disagree with its own reasoning, and there
 * would be no way to tell which half was wrong.
 */
export function contentPoints(coverages: Coverage[]): number {
  const full = coverages.filter((c) => c === "full").length;
  const partial = coverages.filter((c) => c === "partial").length;

  // The published table is written for exactly three guideline points, which is
  // what every telc brief has.
  if (coverages.length !== 3) {
    const max = coverages.length * 2;
    if (max === 0) return 0;
    return Math.min(5, Math.round((5 * (full * 2 + partial)) / max));
  }

  if (full === 3) return 5; //  ++ ++ ++
  if (full === 2 && partial === 1) return 4; //  ++ ++ +
  if (full === 2) return 3; //  ++ ++ Ø
  if (full === 1 && partial === 2) return 3; //  ++ +  +
  if (full === 0 && partial === 3) return 3; //  +  +  +
  if (full === 1 && partial === 1) return 2; //  ++ +  Ø
  if (full === 0 && partial === 2) return 2; //  +  +  Ø
  if (full === 1) return 2; //  ++ Ø  Ø
  if (partial === 1) return 1; //  +  Ø  Ø
  return 0; //  Ø  Ø  Ø
}

/** Clamps whatever the model returned into the band scale it was given. */
export const clampMark = (n: unknown): number =>
  Math.max(
    0,
    Math.min(
      5,
      Math.round(typeof n === "number" && Number.isFinite(n) ? n : 0),
    ),
  );

/**
 * The four marks, and their total under the off-topic rule.
 *
 * Off topic is not "criterion I scored zero": the footnote on p. 34 zeroes
 * II, III and IV as well, because there was nothing on task to mark them on.
 */
export function marksOf(
  criteria: Record<WritingCriterionId, CriterionVerdict>,
  contentMark: number,
  offTopic: boolean,
): { marks: Record<WritingCriterionId, number>; points: number } {
  const marks = Object.fromEntries(
    WRITING_CRITERIA.map((c) => [
      c.id,
      offTopic
        ? 0
        : c.id === "tresc"
          ? contentMark
          : clampMark(criteria[c.id]?.points),
    ]),
  ) as Record<WritingCriterionId, number>;

  const points = offTopic
    ? 0
    : Math.min(
        WRITING_MAX,
        WRITING_CRITERIA.reduce((sum, c) => sum + marks[c.id], 0),
      );
  return { marks, points };
}
